import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import find from 'lodash/find';
import values from 'lodash/values';
import groupBy from 'lodash/groupBy';
import isUndefined from 'lodash/isUndefined';
import {
  Button,
  Grid,
  Paper,
  FormControl,
} from '@mui/material';
import { Stop, Today } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import moment from 'moment';
import 'moment-timezone';
import MUIDataTable from 'mui-datatables';
import React from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { tableOptions } from '../../../lib/table-utils';
import WrappedSpinner from '../../common/wrapped-spinner';
import zoneActions from '../../../actions/zones';
import marketActions from '../../../actions/markets';
import reportActions from '../../../actions/reports';
import withPersistentSettings from '../../common/table-with-persistent-settings';
import { MarketSelect } from '../../common/market-select';
import saveLocalSettingsWrapper from './save-report-settings';

const LOCAL_STORAGE_KEY = 'zone-deployment-report-table';
const styles = {
  root: {
    width: '95%',
    height: '95%',
    marginLeft: 'auto',
    marginRight: 'auto',
    overflowX: 'auto',
  },
};
const TaskTypeNames = { PICK_UP: 'Pick up', DROP_OFF: 'Drop off' };

function getMomentMillis(date) {
  return moment(date).format('x');
}

function eventDataReducer(accum, zoneEvent) { /* eslint-disable no-param-reassign */
  if (get(zoneEvent, 'zone.name')) {
    accum.name = zoneEvent.zone.name;
  }
  if (get(zoneEvent, 'zone.hexColor')) {
    accum.color = zoneEvent.zone.hexColor;
  }

  const reason = get(zoneEvent, 'task.task');
  if (reason === TaskTypeNames.PICK_UP) {
    accum.actualPickup = get(accum, 'actualPickup', 0) + get(zoneEvent, 'task.scannedVehicleIds', []).length;
    accum.pickupCompleted = zoneEvent.createdAt;
  } else if (reason === TaskTypeNames.DROP_OFF) {
    accum.actualDropoff = get(accum, 'actualDropoff', 0) + get(zoneEvent, 'task.scannedVehicleIds', []).length;
    accum.dropoffCompleted = zoneEvent.createdAt;
    accum.plannedDropoff = get(accum, 'plannedDropoff', 0) + zoneEvent.task.vehicleCount || 0;
  }
  return accum;
}

function formatEventData(zoneDataLine, tz) {
  return [
    zoneDataLine.name,
    zoneDataLine.color,
    get(zoneDataLine, 'plannedDropoff', 0),
    get(zoneDataLine, 'actualDropoff', 0),
    zoneDataLine.dropoffCompleted
      ? moment.tz(zoneDataLine.dropoffCompleted, tz).format('YYYY-MM-DD hh:mm:ss a')
      : '',
    get(zoneDataLine, 'actualPickup', 0),
    zoneDataLine.pickupCompleted
      ? moment.tz(zoneDataLine.pickupCompleted, tz).format('YYYY-MM-DD hh:mm:ss a')
      : '',
  ];
}

function processTasks(tasks) {
  return values(groupBy(tasks, (task) => task.opsZoneId))
    .map((tasksPerZone) => tasksPerZone.reduce(eventDataReducer, {}));
}

function formatTableData(tableData, tz) {
  return tableData.map((eventData) => formatEventData(eventData, tz));
}

function getTotalData(tableData) {
  const totalData = tableData.reduce((accum, tableRow) => {
    accum.plannedDropoff += get(tableRow, 'plannedDropoff', 0);
    accum.actualDropoff += get(tableRow, 'actualDropoff', 0);
    accum.actualPickup += get(tableRow, 'actualPickup', 0);
    return accum;
  }, { plannedDropoff: 0, actualDropoff: 0, actualPickup: 0 });
  totalData.name = 'Total';
  totalData.color = '';
  totalData.pickupCompleted = '';
  totalData.dropoffCompleted = '';

  return totalData;
}

class ZoneDeploymentTable extends React.Component {
  constructor(props) {
    super(props);

    const { date, marketId } = props;

    this.state = {
      date: date ? moment.utc(date) : moment.utc(),
      marketId: marketId || '',
    };

    this.handleChange = this.handleChange.bind(this);
    this.fetchData = this.fetchData.bind(this);
    this.handleMarketChange = this.handleMarketChange.bind(this);
    this.saveSettings = this.saveSettings.bind(this);
  }

  async componentDidMount() {
    const dataToFetch = [];
    const {
      markets, zones, fetchActiveMarkets, fetchZones,
    } = this.props;
    const activeMarkets = get(markets, 'activeMarkets.length') > 0;
    if (!activeMarkets) dataToFetch.push(fetchActiveMarkets());
    if (zones || zones.length === 0) dataToFetch.push(fetchZones());
    await Promise.all(dataToFetch);
  }

  async componentDidUpdate(prevProps, prevState) {
    const { date, marketId } = this.state;
    if (isUndefined(date)) return;
    const oldDate = prevState.date;
    const oldMarketId = prevState.marketId;
    if (moment(oldDate).isSame(date) && oldMarketId === marketId) return;

    if (marketId && marketId.length > 0) {
      await this.fetchData();
    }
  }

  get tableOptions() {
    const basicOptions = get(this, 'props.tableOptions', {});
    return { ...basicOptions, ...tableOptions };
  }

  get columns() {
    const { selectedFilters, primarySort, addToColumnData } = this.props;

    const columnData = [
      { name: 'Zone' },
      {
        name: 'Color',
        options: {
          customBodyRender: (data) => (
            data ? <Stop style={{ color: data }} /> : ''
          ),
        },
      },
      { name: 'Planned Dropoff' },
      { name: 'Actual Dropoff' },
      {
        name: 'Dropoff Completed',
        options: {
          customBodyRender: (data) => (
            data || <Today />
          ),
        },
      },
      { name: 'Actual Pickup' },
      {
        name: 'Pickup Completed',
        options: {
          customBodyRender: (data) => (
            data || <Today />
          ),
        },
      },
    ];
    return addToColumnData(columnData, selectedFilters, primarySort);
  }

  async fetchData() {
    const { fetchDeploymentTasks } = this.props;
    const { date, marketId } = this.state;
    await fetchDeploymentTasks({ date: getMomentMillis(date), market_id: marketId });
  }

  saveSettings() {
    const { saveReportSettings } = this.props;
    const {
      date, marketId,
    } = this.state;
    const savedSettings = {
      date: moment.utc(date).format(), marketId,
    };
    saveReportSettings(savedSettings);
  }

  handleChange(event) {
    this.setState({ [event.target.name]: event.target.value }, () => this.saveSettings());
  }

  handleMarketChange(newMarketId) {
    this.setState({ marketId: newMarketId }, () => this.saveSettings());
  }

  render() {
    const {
      reports, markets, zones, getTitleFromData,
    } = this.props;
    const { marketId } = this.state;
    let { date } = this.state;
    date = date || new Date();
    const isLoading = reports.isLoading || markets.isLoading || zones.isLoading;
    const currentMarket = find(get(markets, 'activeMarkets', []), (mkt) => mkt._id === marketId);
    const currentTz = get(currentMarket, 'tz', '');

    const tasks = get(reports.deploymentReport, `${marketId}${getMomentMillis(date)}`);
    const tableData = processTasks(tasks);
    const totalData = getTotalData(tableData, currentTz);
    const displayedZoneData = isEmpty(tableData)
      ? []
      : formatTableData([...tableData, totalData], currentTz);

    return (
      <WrappedSpinner isLoading={isLoading}>
        <Paper style={styles.root} sx={{ mt: 3, pt: 1 }}>
          <Grid container>
            <Grid item xs={3}>
              <MarketSelect value={marketId} handleChange={this.handleMarketChange} />
            </Grid>
            <Grid item xs={3} style={{ padding: '0 8px' }}>
              <FormControl component="fieldset" style={{ width: '100%' }}>
                <DatePicker
                  value={date}
                  name="date"
                  label="Date"
                  autoOk
                  onChange={(newDate) => this.handleChange({ target: { name: 'date', value: newDate } })}
                />
              </FormControl>
            </Grid>
            <Grid item xs={3}>
              {!isLoading
              && (
                <Button variant="contained" color="primary" onClick={async () => this.fetchData()}>
                  Reload
                </Button>
              )}
            </Grid>
            <Grid item xs={12} sx={{ mt: 1 }}>
              <MUIDataTable
                title={getTitleFromData('Zone Deployments', displayedZoneData)}
                columns={this.columns}
                data={displayedZoneData}
                options={this.tableOptions}
              />
            </Grid>
          </Grid>
        </Paper>
      </WrappedSpinner>
    );
  }
}

const mapDispatchToProps = (dispatch) => bindActionCreators({
  ...zoneActions,
  ...marketActions,
  ...reportActions,
}, dispatch);

const mapStateToProps = ({ markets, zones, reports }) => ({
  markets, zones, reports,
});

export default saveLocalSettingsWrapper(
  withPersistentSettings(
    connect(
      mapStateToProps,
      mapDispatchToProps,
    )(withRouter(ZoneDeploymentTable)),
    LOCAL_STORAGE_KEY,
  ),
  LOCAL_STORAGE_KEY,
);
